/**
 * Confirm for the child tile's ✕ on the combined overview (client 2026-09-07,
 * reversing the 2026-08-27 "children are edited on the applicant's own form"
 * rule). Children are step data, not applications: the `removeChild` intent
 * strips the (name, DOB) row from EVERY applicant's property step so the
 * household-max chip can't resurrect it from a co-applicant's copy. Nothing
 * else changes — no status, no detach, no report run. Like the member ✕,
 * there is no regenerate prompt: an already-generated family report keeps
 * the old headcount until someone regenerates it, and the copy says so.
 */
import { ArrowRight } from 'lucide-react'
import { useFetcher } from 'react-router'
import { childAgeLabel } from '#app/utils/application-detail-v2/display'
import type {
	DetailActionResult,
	HouseholdChild,
} from '#app/utils/application-detail-v2/types'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
} from '../dialogs/dialog-bits'

export function RemoveChildDialog({
	open,
	onOpenChange,
	child,
	entryApplicationId,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** The child targeted by the ✕; null while the dialog is closed. */
	child: HouseholdChild | null
	/** The page's entry application — the intent resolves the household from it. */
	entryApplicationId: string
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const busy = fetcher.state !== 'idle'
	// Name the child the way the tile does: name when given, else the age.
	const age = childAgeLabel(child?.dateOfBirth, new Date())
	const label =
		child?.name?.trim() || (age ? `this child (${age})` : 'this child')

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Child removed"
				onSuccess={() => onOpenChange(false)}
			/>
			<DetailDialog
				open={open}
				onOpenChange={onOpenChange}
				title={`Remove ${label} from this household?`}
				description="This removes the child from every applicant's submitted household details. A combined report that was already generated keeps the previous headcount until it is regenerated."
				className="sm:max-w-md"
			>
				<InlineActionError fetcher={fetcher} open={open} />
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone="danger"
						disabled={busy || !child}
						onClick={() => {
							if (!child) return
							const form: Record<string, string> = {
								intent: 'removeChild',
								applicationId: entryApplicationId,
							}
							if (child.name) form.name = child.name
							if (child.dateOfBirth) form.dateOfBirth = child.dateOfBirth
							void fetcher.submit(form, { method: 'post' })
						}}
					>
						{busy ? 'Removing...' : 'Remove child'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
